import type { LeadTimeEntry } from '../../types';

const DAY_MS = 24 * 60 * 60 * 1000;

function startOfLocalDay(d: Date): number {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x.getTime();
}

/** Stable key for Recharts dataKey (Firestore ids can contain chars Recharts dislikes). */
export function userSeriesKey(userId: string): string {
  return `u_${userId.replace(/[^a-zA-Z0-9]/g, '_')}`;
}

export interface LeadTimeChartSeries {
  key: string;
  userId: string;
  displayName: string;
  color: string;
}

const SERIES_COLORS = ['#60a5fa', '#34d399', '#fbbf24', '#f472b6', '#a78bfa', '#fb923c', '#2dd4bf', '#e879f9'];

export interface BuildLeadTimeChartResult {
  chartData: Record<string, string | number>[];
  series: LeadTimeChartSeries[];
  empty: boolean;
}

/**
 * Bucket minutes by calendar day (local) and user. Fills in zero days between first and last activity.
 */
export function buildLeadTimeDailyChartData(entries: LeadTimeEntry[]): BuildLeadTimeChartResult {
  if (!entries.length) {
    return { chartData: [], series: [], empty: true };
  }

  const userMeta = new Map<string, string>();
  entries.forEach(e => {
    if (!userMeta.has(e.userId)) userMeta.set(e.userId, e.userName || 'User');
  });

  const userIds = Array.from(userMeta.keys()).sort();

  let minDay = Infinity;
  let maxDay = -Infinity;
  entries.forEach(e => {
    const t = startOfLocalDay(e.startTime);
    minDay = Math.min(minDay, t);
    maxDay = Math.max(maxDay, t);
  });

  const days: number[] = [];
  for (let t = minDay; t <= maxDay; t += DAY_MS) {
    days.push(t);
  }

  const minutesByDayUser = new Map<number, Map<string, number>>();
  days.forEach(d => {
    const m = new Map<string, number>();
    userIds.forEach(uid => m.set(uid, 0));
    minutesByDayUser.set(d, m);
  });

  entries.forEach(e => {
    const dk = startOfLocalDay(e.startTime);
    const bucket = minutesByDayUser.get(dk);
    if (!bucket) return;
    const cur = bucket.get(e.userId) || 0;
    bucket.set(e.userId, cur + e.minutes);
  });

  const series: LeadTimeChartSeries[] = userIds.map((uid, i) => ({
    key: userSeriesKey(uid),
    userId: uid,
    displayName: userMeta.get(uid) || 'User',
    color: SERIES_COLORS[i % SERIES_COLORS.length],
  }));

  const useCompactDates = days.length > 12;

  const chartData = days.map(t => {
    const row: Record<string, string | number> = {
      dateKey: t,
      dateLabel: formatChartDayLabel(t, useCompactDates),
    };
    const bucket = minutesByDayUser.get(t)!;
    userIds.forEach(uid => {
      row[userSeriesKey(uid)] = Math.round((bucket.get(uid) || 0) * 10) / 10;
    });
    return row;
  });

  return { chartData, series, empty: false };
}

/** Compact "3/10" for crowded axes; longer ranges use this automatically. */
function formatChartDayLabel(ts: number, compact: boolean): string {
  const d = new Date(ts);
  if (compact) {
    return `${d.getMonth() + 1}/${d.getDate()}`;
  }
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
